/**
 * Mapping Layer: Canonical Payload → onboardingStore
 *
 * Separate write layer that bridges the canonicalized intake data
 * into the existing Zustand store. Single atomic write — no partial state.
 */

import type { CanonicalPayload } from './canonicalizeIntakeExtraction';

interface ExtractionMeta {
  model: string;
  extraction_version: number;
  classification: {
    decision_type: string;
    decision_subtype: string;
    confidence: number;
    classification_signals: string[];
    preliminary_impact_level: string;
    affected_roles: string[];
  };
}

interface OnboardingStoreActions {
  setDecisionArea: (type: string) => void;
  setDecisionSubtype: (subtype: string) => void;
  setSessionTitle: (title: string) => void;
  setStructuredTrigger: (optionA: string, optionB: string) => void;
  setInitialPreference: (pref: 'a' | 'b' | 'undecided') => void;
  setScores: (impact: number, urgency: number) => void;
  setClassificationSource: (
    systemType: string,
    systemSubtype: string,
    confidence: number,
    inputHash?: string | null,
    model?: string | null,
  ) => void;
  setClassificationOverridden: (overridden: boolean) => void;
  setClassificationSignals: (signals: string[]) => void;
  setPreliminaryImpactLevel: (level: string | null) => void;
  setAffectedRoles: (roles: string[]) => void;
  setStructuredStakes: (data: {
    wrongChoiceA: string;
    wrongChoiceB: string;
    reversibilityA: string;
    reversibilityB: string;
    timeframeA: string;
    timeframeB: string;
    inaction: string;
    inactionTimeframe: string;
    worstCase: string;
    worstCaseReason: string;
    worstCaseTrigger: string;
    worstCaseProbability: string;
  }) => void;
  setPreviousAttempts: (attempts: string) => void;
  setTimeHorizonDays: (days: number | null) => void;
  setRiskToleranceLevel: (level: 'conservative' | 'moderate' | 'aggressive' | null) => void;
  setSuccessDefinition: (def: string) => void;
  setPrimaryMetric: (metric: string) => void;
  setTrigger: (trigger: string) => void;
}

export function mapCanonicalToStore(
  payload: CanonicalPayload,
  extractionMeta: ExtractionMeta,
  store: OnboardingStoreActions,
  rawInput?: string,
): void {
  // ── Core decision ──
  store.setDecisionArea(payload.decisionType);
  store.setDecisionSubtype(payload.decisionSubtype || extractionMeta.classification.decision_subtype || '');
  store.setSessionTitle(payload.sessionTitle);
  store.setStructuredTrigger(payload.optionA, payload.optionB);
  store.setInitialPreference(payload.initialPreference);

  // ── Scores ──
  store.setScores(payload.impactScore, payload.urgencyScore);

  // ── Classification audit trail ──
  store.setClassificationSource(
    extractionMeta.classification.decision_type || payload.decisionType,
    extractionMeta.classification.decision_subtype || '',
    extractionMeta.classification.confidence ?? 0,
    null,
    extractionMeta.model,
  );
  store.setClassificationOverridden(false);
  store.setClassificationSignals(extractionMeta.classification.classification_signals || []);
  store.setPreliminaryImpactLevel(extractionMeta.classification.preliminary_impact_level || null);
  store.setAffectedRoles(extractionMeta.classification.affected_roles || []);

  // ── Stakes (soft fields) — always set to ensure StepAnalysisPreview has data ──
  store.setStructuredStakes({
    wrongChoiceA: payload.stakesWrongChoiceA || '',
    wrongChoiceB: payload.stakesWrongChoiceB || '',
    reversibilityA: '',
    reversibilityB: '',
    timeframeA: '6',
    timeframeB: '6',
    inaction: payload.stakesInaction || 'Keine spezifischen Stakes erfasst.',
    inactionTimeframe: '6',
    worstCase: payload.worstCase || '',
    worstCaseReason: '',
    worstCaseTrigger: '',
    worstCaseProbability: 'mittel',
  });

  // ── Soft fields ──
  if (payload.previousAttempts) store.setPreviousAttempts(payload.previousAttempts);
  if (payload.timeHorizonDays && payload.timeHorizonDays > 0) store.setTimeHorizonDays(payload.timeHorizonDays);
  if (payload.riskToleranceLevel) store.setRiskToleranceLevel(payload.riskToleranceLevel);
  if (payload.successDefinition) store.setSuccessDefinition(payload.successDefinition);
  if (payload.primaryMetric) store.setPrimaryMetric(payload.primaryMetric);

  // ── BUG 6 FIX: Raw input → trigger field as clean raw text (not mixed with options) ──
  if (rawInput) {
    store.setTrigger(rawInput.slice(0, 2000));
  }
}
